import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text, Icon } from 'components/ui';
import {
    colors, space, fontSizes, fontWeights, radii, mediaQueries, borders, shadows, lineHeights
} from 'style/config';
import ProductImage from 'components/Product/ProductImage';

const ProductCard = ({ product, index, onRemove, getText }) => {
    const handleRemove = () => {
        if (onRemove) {
            onRemove(product.productId);
        }
    };

    return (
        <Box css={styles.productCard}>
            <Box css={styles.productImageContainer}>
                <ProductImage
                    id={product?.currentSku?.skuId}
                    skuImages={product?.heroImage ? { image: product?.heroImage } : product?.currentSku?.skuImages}
                    altText={product?.currentSku?.imageAltText || product?.displayName || ''}
                    size={[74, 74, 74]}
                    disableLazyLoad={true}
                    objectFit='cover'
                    src={product?.heroImage}
                />
            </Box>
            <Box css={styles.productInfoContainer}>
                {product?.brandName && <Text css={styles.productBrandName}>{product.brandName}</Text>}
                {product?.displayName && (
                    <Text
                        css={styles.productDisplayName}
                        numberOfLines={2}
                    >
                        {product.displayName}
                    </Text>
                )}
                {product?.currentSku?.listPrice && <Text css={styles.productPrice}>{product?.currentSku?.listPrice}</Text>}
            </Box>
            <button
                type='button'
                onClick={handleRemove}
                aria-label={getText('removeProduct')}
                css={styles.removeButton}
                data-at={Sephora.debug.dataAt(`compare_drawer_remove_${index + 1}`)}
            >
                <Icon
                    name='x'
                    size={14}
                    aria-hidden={true}
                />
            </button>
        </Box>
    );
};

ProductCard.propTypes = {
    product: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    onRemove: PropTypes.func.isRequired,
    getText: PropTypes.func.isRequired
};

const styles = {
    productCard: {
        position: 'relative',
        width: '100%',
        height: '100%',
        borderRadius: `${radii[2]}px`,
        overflow: 'visible',
        backgroundColor: colors.white,
        border: `${borders[1]} ${colors.lightGray}`,
        boxShadow: shadows.light,
        display: 'flex',
        flexDirection: 'column',
        [mediaQueries.lg]: {
            flexDirection: 'row',
            padding: `${space[4]}px`,
            gap: `${space[2]}px`,
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            height: '106px',
            boxSizing: 'border-box'
        }
    },
    productImageContainer: {
        width: '100%',
        height: '100%',
        position: 'relative',
        [mediaQueries.lg]: {
            width: '74px',
            height: '74px',
            flexShrink: 0
        }
    },
    productInfoContainer: {
        display: 'none',
        flex: '1 0 0',
        minWidth: 0,
        flexDirection: 'column',
        gap: '2px',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        [mediaQueries.lg]: {
            display: 'flex'
        }
    },
    productBrandName: {
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        color: colors.black,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    productDisplayName: {
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.normal,
        lineHeight: lineHeights.tight,
        color: colors.black
    },
    productPrice: {
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        color: colors.black,
        marginTop: 'auto'
    },
    removeButton: {
        position: 'absolute',
        top: `${space[1]}px`,
        right: `${space[1]}px`,
        width: '14px',
        height: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0,
        zIndex: 10,
        background: 'none',
        border: 'none',
        color: colors.black,
        [mediaQueries.lg]: {
            top: '0',
            right: '0',
            position: 'relative'
        }
    }
};

export default wrapFunctionalComponent(ProductCard, 'ProductCard');
